"use client";

import { useAuth } from "@clerk/nextjs";
import { usePathname, useSearchParams } from "next/navigation";
import { PiPencil, PiSignIn } from "react-icons/pi";

import { PAGES } from "@/configs/pages.config";
import { buildAuthPagePath, FILES_URL_PARAM } from "@/lib/auth-redirect";
import { DEFAULT_LOCALE, getLocaleFromPathname, SupportedLocale } from "@/lib/i18n/locales";
import { getNavigationCopy } from "@/lib/i18n/navigation";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { TRIAL_LINK_CLAIM_PARAM } from "@/lib/trial-link-claim";
import { cn } from "@/lib/utils";

import { buttonVariants } from "../button/button";
import Link from "../link/link";
import UserButton from "../user-button/user-button";
import LanguageSwitcher from "./language-switcher";

export default function NavigationActions() {
  const { isSignedIn } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = pathname ? getLocaleFromPathname(pathname) : DEFAULT_LOCALE;
  const copy = getNavigationCopy(locale);
  const mobileActionCopy = MOBILE_ACTION_COPY[locale] ?? MOBILE_ACTION_COPY[DEFAULT_LOCALE];

  /** Clerk not loaded yet */
  if (isSignedIn === undefined) {
    return <div className="min-w-36"></div>;
  }

  if (isSignedIn) {
    const dashboardPath = withSeoLocalePath(locale, PAGES.FILES.PATH);
    return (
      <div className="flex items-center gap-2 sm:gap-4">
        <LanguageSwitcher />
        <UserButton type="navigation" />
        <Link
          href={dashboardPath}
          prefetch={false}
          color="white-primary"
          className="h-[var(--navbar-height)] text-[11px] sm:text-base whitespace-nowrap"
        >
          <span className="sm:hidden">{mobileActionCopy.dashboard}</span>
          <span className="hidden sm:inline">{copy.actions.dashboard}</span>
        </Link>
      </div>
    );
  }

  const rawFileUrl = searchParams.get(FILES_URL_PARAM)?.trim();
  const trialLinkClaimToken = searchParams.get(TRIAL_LINK_CLAIM_PARAM)?.trim();
  const loginPath = buildAuthPagePath(withSeoLocalePath(locale, PAGES.LOGIN.PATH), {
    fileUrl: rawFileUrl,
    trialLinkClaimToken,
  });
  const signupPath = buildAuthPagePath(withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), {
    fileUrl: rawFileUrl,
    trialLinkClaimToken,
  });

  return (
    <div className="flex items-center gap-1 sm:gap-4">
      <LanguageSwitcher />
      <Link
        href={loginPath}
        color="plain"
        aria-label={copy.actions.login}
        className={cn(buttonVariants({ variant: "tertiary" }), "shadow-none text-xs sm:text-sm p-2 rounded-full")}
      >
        <PiSignIn className="hidden sm:inline-flex w-5 h-5" />
        <span className="text-[11px] sm:text-sm sm:hidden">{mobileActionCopy.login}</span>
        <span className="hidden sm:inline">{copy.actions.login}</span>
      </Link>
      <Link href={signupPath} color="white-primary" aria-label={copy.actions.signup} className="text-xs sm:text-sm">
        <PiPencil className="hidden sm:inline-flex w-5 h-5" />
        <span className="text-[11px] sm:text-sm sm:hidden">{mobileActionCopy.signup}</span>
        <span className="hidden sm:inline">{copy.actions.signup}</span>
      </Link>
    </div>
  );
}

const MOBILE_ACTION_COPY: Record<SupportedLocale, { login: string; signup: string; dashboard: string }> = {
  en: { login: "Login", signup: "Sign up", dashboard: "Dashboard" },
  es: { login: "Entrar", signup: "Registro", dashboard: "Panel" },
  fr: { login: "Connexion", signup: "Inscription", dashboard: "Tableau" },
  de: { login: "Login", signup: "Registrieren", dashboard: "Dashboard" },
  it: { login: "Accedi", signup: "Iscriviti", dashboard: "Dashboard" },
  pt: { login: "Entrar", signup: "Cadastrar", dashboard: "Painel" },
};
